import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { environment } from '../../environments/environment';

/**
 * Purely local, per-browser ad-free state.
 *
 * When the user watches a rewarded video to completion, their own ads vanish
 * for a configured duration (1h in prod, shorter in dev). No RTDB, no
 * serverTimeOffset, no cloud function — only this client is affected, so
 * Date.now() is sufficient. See handover §3 / §5c.
 *
 * The expiry epoch is persisted in localStorage so the earned window survives a
 * full page load (F5, following a link, PWA reopen, and — because each locale is
 * its own bundle — a language switch) and is shared across tabs of the same
 * browser. Still purely client-local; nothing leaves the browser.
 */
@Injectable()
export class AdFreeService {
    private static readonly STORAGE_KEY = 'sp_adFreeUntil';

    private adFreeUntil = 0;
    private timer: any;
    readonly adFree$ = new BehaviorSubject<boolean>(false);

    constructor(private logger: LoggerService, private ngZone: NgZone) {
        // Rehydrate an ad-free window still running from a previous page load.
        const stored = this.readStoredUntil();
        if (stored > Date.now()) {
            this.startWindow(stored);
        } else if (stored > 0) {
            this.clearStoredUntil(); // expired while the tab was gone
        }
    }

    get isAdFree(): boolean {
        return Date.now() < this.adFreeUntil;
    }

    /** Grant the reward: hide this client's ads for `durationMs` (default from env). */
    grantAdFree(durationMs: number = environment.adFreeDurationMs ?? 3_600_000): void {
        // Providers may resolve outside Angular's zone (the AppLixir SDK fires its
        // callbacks outside NgZone). Run the state change in-zone so the CTA and the
        // ad-slot *ngIf bindings re-evaluate; also keeps the expiry timer in-zone.
        this.ngZone.run(() => this.startWindow(Date.now() + durationMs));
    }

    /**
     * Arm (or re-arm) the ad-free window until the given epoch, persist it, and
     * schedule expiry for the REMAINING time — shared by a fresh grant and by
     * rehydration on construction (where less than the full duration is left).
     */
    private startWindow(until: number): void {
        clearTimeout(this.timer);
        const remaining = until - Date.now();
        if (remaining <= 0) {
            return;
        }
        this.adFreeUntil = until;
        this.writeStoredUntil(until);
        this.logger.log('AdFreeService: ad-free until epoch', until);
        this.adFree$.next(true);
        this.timer = setTimeout(() => {
            this.logger.log('AdFreeService: ad-free window expired');
            this.adFreeUntil = 0;
            this.clearStoredUntil();
            this.adFree$.next(false);
        }, remaining);
    }

    private readStoredUntil(): number {
        try {
            const raw = localStorage.getItem(AdFreeService.STORAGE_KEY);
            const parsed = raw ? Number(raw) : 0;
            return Number.isFinite(parsed) ? parsed : 0;
        } catch {
            return 0; // storage disabled / private mode / SecurityError
        }
    }

    private writeStoredUntil(until: number): void {
        try {
            localStorage.setItem(AdFreeService.STORAGE_KEY, String(until));
        } catch {
            // best-effort persistence; the in-memory timer still governs this tab
        }
    }

    private clearStoredUntil(): void {
        try {
            localStorage.removeItem(AdFreeService.STORAGE_KEY);
        } catch {
            // ignore — nothing to clean up if storage is unavailable
        }
    }
}
